import { toast } from 'sonner'
import { useState } from 'react'
import { format } from 'date-fns'
import { Game } from '@/types/globals'
import { RootState } from '@/store/store'
import { trackEvent } from '@/lib/trackEvent'
import { useNavigate } from 'react-router-dom'
import { axios } from '@/services/api/axiosInstance'
import { endpoints } from '@/services/api/endpoints'
import { useDispatch, useSelector } from 'react-redux'
import { toggleGameDelete } from '@/store/features/systemSlice'
import { updateGameConfig } from '@/store/features/gameConfigSlice'
import { MdDelete, MdEdit, MdSave, MdShare } from 'react-icons/md'

const GameCard = ({
  game,
  setGames,
}: {
  game: Game
  setGames: (games: Game[]) => void
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [, setCopied] = useState(false)
  const [editedTitle, setEditedTitle] = useState('')
  const [editingGameId, setEditingGameId] = useState('')
  const gameConfig = useSelector((state: RootState) => state.gameConfig)

  const handleCopy = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(
        `https://play.aicade.io/${textToCopy}`
      )
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('Game Playable URL Copied !')
    } catch (err) {
      console.error('Failed to copy: ', err)
      toast.error('Copied Failed !')
    }
  }

  const handleEditTitle = (gameId: string, currentTitle: string) => {
    setEditingGameId(gameId)
    setEditedTitle(currentTitle)
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTitle(e.target.value)
  }

  const handleSaveTitle = async (gameId: string) => {
    if (editedTitle.trim() === game?.game_title) {
      setEditingGameId('')
      return
    }
    const updatedGameConfig = { ...gameConfig, title: editedTitle }
    //  await dispatch(updateTitle(editedTitle))
    await dispatch(updateGameConfig(updatedGameConfig))

    try {
      const formData = new FormData()
      formData.append('game_id', gameId)
      formData.append('game_config', JSON.stringify(updatedGameConfig))
      // formData.append('game_config', JSON.stringify(gameConfig))
      await axios.put(endpoints.saveGameData.url, formData)
    } catch (e) {
      console.log(e)
    }
    try {
      const response = await axios.patch(
        endpoints.gameUpdate.url,
        { game_title: editedTitle },
        { params: { game_id: gameId } }
      )
      console.log(response.data)
      trackEvent('Game Title Updated', { newTitle: editedTitle })
      // Update the game title locally
      setGames((prevGames: Game[]) =>
        prevGames.map((game: Game) =>
          game.game_id === gameId ? { ...game, game_title: editedTitle } : game
        )
      )
    } catch (error) {
      console.error('Error updating game title:', error)
      toast.error('Error updating game title')
    } finally {
      setEditingGameId('')
    }
    setEditingGameId('')
    toast.success('Game updated!')
  }

  return (
    <div
      onClick={editingGameId ? () => null : () => navigate(`/${game?.game_id}`)}
      className="group flex cursor-pointer flex-col gap-4 overflow-hidden rounded-lg bg-black/50 p-2 text-white/60 backdrop-blur-lg"
    >
      <img
        alt={game?.game_id}
        src={
          game?.cover_image && game?.cover_image !== 'null'
            ? game?.cover_image
            : '/placeholder.webp'
        }
        className="common-transition aspect-video h-40 w-full overflow-hidden rounded-lg object-cover group-hover:scale-95"
      />
      <div className="flex flex-col gap-2">
        <h3 className="flex w-full items-center gap-2 overflow-hidden font-semibold text-white">
          {editingGameId ? (
            <>
              <input
                autoFocus
                value={editedTitle}
                className="h-8 flex-1 rounded-lg bg-white/25 px-4 text-xs focus:outline-none"
                onChange={handleTitleChange}
                onBlur={() => handleSaveTitle(game.game_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveTitle(game.game_id)
                  }
                }}
              />
              <MdSave
                size={14}
                onClick={(e) => {
                  e.stopPropagation()
                  handleSaveTitle(game?.game_id)
                }}
              />
            </>
          ) : (
            <>
              <span className="flex-1 truncate">{game?.game_title}</span>
              <MdEdit
                size={14}
                onClick={(e) => {
                  e.stopPropagation()
                  handleEditTitle(game?.game_id, game?.game_title)
                }}
              />
            </>
          )}
        </h3>
        <div className="flex items-center justify-between gap-2 text-xs font-medium">
          <span>{format(new Date(game?.ai_created), 'dd MMM yyyy')}</span>
          <span
            className={`${game?.publish_status ? 'text-green-500' : 'text-yellow-500'}`}
          >
            {game?.publish_status ? 'Published' : 'Draft'}
          </span>
        </div>
        <div className="flex cursor-pointer items-center justify-between gap-2 text-sm">
          <span className="capitalize">{game?.device_orientation}</span>
          <span className="flex items-center gap-4">
            <button
              className="text-white"
              onClick={(e) => {
                e.stopPropagation()
                handleCopy(game?.game_id)
              }}
            >
              <MdShare size={20} />
            </button>
            {!game?.publish_status && (
              <button
                className="text-red-500"
                onClick={(e) => {
                  e.stopPropagation()
                  dispatch(toggleGameDelete({ id: game?.game_id, show: true }))
                }}
              >
                <MdDelete size={20} />
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

export default GameCard
